import { PUSH_RETRY_FOR_PERIOD, PUSH_RETRY_TYPE } from '@mgCommon/constants/constants.js';
import { NATIVE_PUSH_CHANNELS, NATIVE_PUSH_GAME_ID } from '@nativePush/constants/constants.js';
import { NATIVE_CHANNEL_TO_FORMDATA_MAPPER } from '@nativePush/constants/mappings.js';
import { createSlice } from '@reduxjs/toolkit';

const defaultDate = new Date().toJSON().slice(0, 10);
const defaultTime = '00:00';

const commonDeviceDefaultData = {
  messageTitle: '',
  messageText: '',
  imageUrl: '',
  iconUrl: '',
  defaultAction: '',
  actionDestination: '',
};
const initialIosData = { ...commonDeviceDefaultData };
const initialAndroidData = { ...commonDeviceDefaultData };

const initialState = {
  campaignName: '',
  channel: '',
  gameId: NATIVE_PUSH_GAME_ID.SB,
  iosData: { ...initialIosData },
  androidData: { ...initialAndroidData },
  startDate: defaultDate,
  startDateTime: defaultTime,
  endDate: '',
  endDateTime: '',
  retryForNumber: 0,
  retryForPeriod: PUSH_RETRY_FOR_PERIOD.WEEKS,
  retryUntilDate: '',
  retryType: PUSH_RETRY_TYPE.RETRY_FOR,
};

const nativePushFormDataSlice = createSlice({
  name: 'nativePushFormData',
  initialState,
  reducers: {
    setNativePushFormData(state, { payload }) {
      const { iosData, androidData } = payload;

      state.campaignName = payload.campaignName;
      state.channel = payload.channel;
      state.gameId = payload.gameId;
      state.iosData = { ...iosData };
      state.androidData = { ...androidData };
      state.startDate = payload.startDate || defaultDate;
      state.startDateTime = payload.startDateTime || defaultTime;
      state.endDate = payload.endDate || '';
      state.endDateTime = payload.endDateTime || '';
      state.retryForNumber = Number(payload.retryForNumber || 0);
      state.retryForPeriod = payload.retryForPeriod;
      state.retryUntilDate = payload.retryUntilDate || '';
      state.retryType = payload.retryType || PUSH_RETRY_TYPE.RETRY_FOR;
    },
    setCampaignName(state, { payload }) {
      state.campaignName = payload.campaignName;
    },
    setChannel(state, { payload }) {
      const { channel } = payload;
      state.channel = channel;

      switch (channel) {
        case NATIVE_PUSH_CHANNELS.IOS:
          state.androidData = { ...initialAndroidData };
          break;
        case NATIVE_PUSH_CHANNELS.ANDROID:
          state.iosData = { ...initialIosData };
          break;
        default:
          state.androidData = { ...initialAndroidData };
          state.iosData = { ...initialIosData };
          break;
      }
    },
    setGameId(state, { payload }) {
      state.gameId = payload.gameId;
    },
    setMessageTitle(state, { payload }) {
      const { channel, messageTitle } = payload;
      state[NATIVE_CHANNEL_TO_FORMDATA_MAPPER[channel]].messageTitle = messageTitle;
    },
    setMessageText(state, { payload }) {
      const { channel, messageText } = payload;
      state[NATIVE_CHANNEL_TO_FORMDATA_MAPPER[channel]].messageText = messageText;
    },
    setImageUrl(state, { payload }) {
      const { channel, imageUrl } = payload;
      state[NATIVE_CHANNEL_TO_FORMDATA_MAPPER[channel]].imageUrl = imageUrl;
    },
    setIconUrl(state, { payload }) {
      const { channel, iconUrl } = payload;
      state[NATIVE_CHANNEL_TO_FORMDATA_MAPPER[channel]].iconUrl = iconUrl;
    },
    setDefaultAction(state, { payload }) {
      const { channel, defaultAction } = payload;
      state[NATIVE_CHANNEL_TO_FORMDATA_MAPPER[channel]].defaultAction = defaultAction;

      if (!defaultAction) {
        state[NATIVE_CHANNEL_TO_FORMDATA_MAPPER[channel]].actionDestination = '';
      }
    },
    setActionDestination(state, { payload }) {
      const { channel, actionDestination } = payload;
      state[NATIVE_CHANNEL_TO_FORMDATA_MAPPER[channel]].actionDestination = actionDestination;
    },
    setStartDate(state, { payload }) {
      state.startDate = payload.startDate;
    },
    setStartDateTime(state, { payload }) {
      state.startDateTime = payload.startDateTime;
    },
    setEndDate(state, { payload }) {
      state.endDate = payload.endDate;
    },
    setEndDateTime(state, { payload }) {
      state.endDateTime = payload.endDateTime;
    },
    setRetryForNumber(state, { payload }) {
      state.retryForNumber = Number(payload.retryForNumber || 0);
    },
    setRetryForPeriod(state, { payload }) {
      state.retryForPeriod = payload.retryForPeriod;
    },
    setRetryUntilDate(state, { payload }) {
      state.retryUntilDate = payload.retryUntilDate;
    },
    setRetryType(state, { payload }) {
      state.retryType = payload.retryType;
    },
  },
});

export const {
  setNativePushFormData,
  setCampaignName,
  setChannel,
  setGameId,
  setMessageTitle,
  setMessageText,
  setImageUrl,
  setIconUrl,
  setDefaultAction,
  setActionDestination,
  setStartDate,
  setStartDateTime,
  setEndDate,
  setEndDateTime,
  setRetryForNumber,
  setRetryForPeriod,
  setRetryUntilDate,
  setRetryType,
} = nativePushFormDataSlice.actions;

export default nativePushFormDataSlice.reducer;
